import React from 'react';
import { DocumentIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ContractServiceItem, QuoteItem, ServiceRate } from '../hooks/useQuoteItems';
import { formatUnitPriceDisplay } from '../utils/formatNumberInput';
import {
  MARKUP_DISPLAY_QUANTITY,
  MARKUP_DISPLAY_UNIT,
  PROCUREMENT_SERVICE_TYPES,
  calculateLineAmounts,
  getQuoteLineDisplay,
  isProcurementServiceType,
  quantityUnitLabel,
  storedRateToPercent,
  summarizeQuoteItemTotals
} from '../utils/quoteItemAmounts';

interface IndexedQuoteItem {
  item: QuoteItem;
  index: number;
}

interface QuoteItemsEditorProps {
  hourRows: IndexedQuoteItem[];
  markupRows: IndexedQuoteItem[];
  useContractOptions: boolean;
  hourContractItems: ContractServiceItem[];
  hourRates: ServiceRate[];
  currentContractId?: string;
  serviceRates: ServiceRate[];
  contractServiceItems: ContractServiceItem[];
  formatCurrency: (amount: number | null | undefined) => string;
  onAddHourItem: () => void;
  onAddMarkupItem: () => void;
  onOpenBulkImport: () => void;
  updateQuoteItem: (index: number, field: keyof QuoteItem, value: string | number | boolean) => void;
  updateItemPrices: (
    index: number,
    serviceType: string,
    serviceRates: ServiceRate[],
    contractServiceItems: ContractServiceItem[],
    contractId?: string
  ) => void;
  removeQuoteItem: (index: number) => void;
}

function quantityInputValue(quantity: number): string {
  if (quantity % 1 === 0) {
    return Math.floor(quantity).toString();
  }
  return quantity.toString();
}

export const QuoteItemsEditor: React.FC<QuoteItemsEditorProps> = ({
  hourRows,
  markupRows,
  useContractOptions,
  hourContractItems,
  hourRates,
  currentContractId,
  serviceRates,
  contractServiceItems,
  formatCurrency,
  onAddHourItem,
  onAddMarkupItem,
  onOpenBulkImport,
  updateQuoteItem,
  updateItemPrices,
  removeQuoteItem
}) => {
  const hourCategoryTotal = summarizeQuoteItemTotals(hourRows.map(({ item }) => item)).hoursAfterDiscount;
  const procurementCategoryTotal = summarizeQuoteItemTotals(markupRows.map(({ item }) => item)).procurementAfterDiscount;

  const handleQuantityChange = (index: number, value: string) => {
    if (value === '') {
      updateQuoteItem(index, 'quantity', 0);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateQuoteItem(index, 'quantity', numValue);
    }
  };

  const handleServiceTypeChange = (index: number, serviceType: string) => {
    updateItemPrices(
      index,
      serviceType,
      serviceRates,
      contractServiceItems,
      currentContractId
    );
  };

  const handleChargePercentChange = (index: number, value: string) => {
    if (value === '') {
      updateQuoteItem(index, 'contract_price', 0);
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateQuoteItem(index, 'unit', '%');
      updateQuoteItem(index, 'contract_price', numValue);
    }
  };

  const handleProcurementTypeChange = (index: number, serviceType: string) => {
    updateQuoteItem(index, 'service_type', serviceType);
    updateQuoteItem(index, 'unit', '%');
  };

  const rowInputClass = (isDeduction: boolean | undefined) =>
    `block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary text-xs ${
      isDeduction ? 'bg-red-50 border-red-200' : ''
    }`;

  const renderServiceTypeSelect = (
    item: QuoteItem,
    index: number,
    contractItems: ContractServiceItem[],
    rates: ServiceRate[]
  ) => (
    <select
      value={item.service_type || ''}
      onChange={(e) => handleServiceTypeChange(index, e.target.value)}
      className={rowInputClass(item.is_deduction)}
    >
      <option value="">選擇服務類型</option>
      {useContractOptions ? (
        contractItems.map(contractItem => (
          <option key={`contract-${contractItem.id}`} value={contractItem.service_type}>
            {contractItem.service_type}（{formatUnitPriceDisplay(contractItem.contract_price, contractItem.unit)}）
          </option>
        ))
      ) : (
        rates.map(rate => (
          <option key={rate.id} value={rate.service_type}>
            {rate.service_type}（{formatUnitPriceDisplay(rate.unit_price || 0, rate.unit)}）
          </option>
        ))
      )}
    </select>
  );

  const renderDeductionCell = (item: QuoteItem, index: number) => (
    <td className="px-4 py-2">
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={item.is_deduction || false}
          onChange={(e) => updateQuoteItem(index, 'is_deduction', e.target.checked)}
          className="h-3 w-3 text-red-600 focus:ring-red-500 border-gray-300 rounded"
          title="勾選表示此項目為扣除項目（負值）"
        />
        <span className="ml-1 text-xs text-gray-500">
          {item.is_deduction ? '扣除' : '收入'}
        </span>
      </div>
    </td>
  );

  const renderItemNameCell = (item: QuoteItem, index: number) => (
    <td className="px-4 py-2">
      <input
        type="text"
        value={item.item_name}
        onChange={(e) => updateQuoteItem(index, 'item_name', e.target.value)}
        className={rowInputClass(item.is_deduction)}
        placeholder={item.is_deduction ? '扣除項目名稱' : '服務項目'}
      />
    </td>
  );

  const renderAfterDiscountCell = (item: QuoteItem, asMarkup = false) => {
    const contractPrice = item.contract_price ?? 0;
    const amounts = calculateLineAmounts({
      listPrice: asMarkup ? contractPrice : (item.unit_price ?? item.list_price ?? 0),
      contractPrice,
      quantity: item.quantity ?? 0,
      unit: asMarkup ? (item.unit || '%') : item.unit,
      forceMarkup: asMarkup,
      isDeduction: item.is_deduction
    });
    return (
      <td className="px-4 py-2">
        <div className={`text-xs font-medium ${item.is_deduction ? 'text-red-600' : 'text-gray-900'}`}>
          {(item.is_deduction ? '-' : '') + formatCurrency(Math.abs(amounts.subtotal_afterdiscount))}
        </div>
      </td>
    );
  };

  const renderRemoveCell = (index: number) => (
    <td className="px-4 py-2">
      <button
        onClick={() => removeQuoteItem(index)}
        className="text-red-600 hover:text-red-900"
        title="刪除項目"
      >
        <TrashIcon className="h-3 w-3" />
      </button>
    </td>
  );

  return (
    <div className="bg-ci-secondary/20 p-4 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-sm font-medium text-primary">報價單明細</h4>
        <div className="space-x-2">
          <button
            onClick={onOpenBulkImport}
            className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
            title="批量匯入報價項目"
          >
            <DocumentIcon className="h-3 w-3 mr-1" />
            批量匯入
          </button>
          <button
            onClick={onAddHourItem}
            className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-primary hover:bg-primary-dark"
          >
            <PlusIcon className="h-3 w-3 mr-1" />
            新增項目
          </button>
          <button
            onClick={onAddMarkupItem}
            className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-xs font-medium text-ci-text bg-ci-button hover:opacity-90"
          >
            <PlusIcon className="h-3 w-3 mr-1" />
            新增採購
          </button>
        </div>
      </div>

      <div className="mb-6">
        <h5 className="text-xs font-medium text-ci-text mb-2">工時／計次項目</h5>
        {hourRows.length === 0 ? (
          <p className="text-gray-500 text-sm">尚未新增工時／計次項目</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">扣除項目</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">服務項目</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">服務類型</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">合約單價</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">數量</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">折扣後小計 (元)</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {hourRows.map(({ item, index }) => (
                  <tr key={`hour-${index}`} className={item.is_deduction ? 'bg-red-50' : ''}>
                    {renderDeductionCell(item, index)}
                    {renderItemNameCell(item, index)}
                    <td className="px-4 py-2">
                      {renderServiceTypeSelect(item, index, hourContractItems, hourRates)}
                    </td>
                    <td className="px-4 py-2">
                      <div className={`text-xs font-medium ${item.is_deduction ? 'text-red-600' : 'text-gray-700'}`}>
                        {item.is_deduction ? '-' : ''}{formatUnitPriceDisplay(Math.abs(item.contract_price || 0), item.unit)}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center space-x-1">
                        <input
                          type="number"
                          min={item.is_deduction ? undefined : '0'}
                          step="0.01"
                          value={quantityInputValue(item.quantity)}
                          onChange={(e) => handleQuantityChange(index, e.target.value)}
                          className={`${rowInputClass(item.is_deduction)} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                        />
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {quantityUnitLabel(item.unit)}
                        </span>
                      </div>
                    </td>
                    {renderAfterDiscountCell(item)}
                    {renderRemoveCell(index)}
                  </tr>
                ))}
                <tr className="bg-gray-50">
                  <td colSpan={5} className="px-4 py-2 text-xs font-medium text-ci-text text-right">工時類別合計</td>
                  <td className="px-4 py-2 text-xs font-semibold text-ci-text">{formatCurrency(hourCategoryTotal)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h5 className="text-xs font-medium text-ci-text mb-2">採購項目</h5>
        <div className="mb-3 rounded-md bg-primary/5 px-3 py-2 text-xs text-ci-text">
          採購與服務收費表分開、無牌價：收費類別僅「境外採購（美金）」「境內採購（台幣）」。收費比例請自行填寫（50 收一半、100 實報實銷、110 含 10% 服務費）。境外採購請填已換算的台幣金額。合約價＝實際採購金額 × 收費比例，數量以 {MARKUP_DISPLAY_QUANTITY} {MARKUP_DISPLAY_UNIT} 計。
        </div>
        {markupRows.length === 0 ? (
          <p className="text-gray-500 text-sm">尚未新增採購項目</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">扣除項目</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">服務項目</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">收費類別</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">實際採購金額</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">收費比例</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                    合約價
                    <div className="normal-case font-normal text-[10px] text-gray-400 tracking-normal mt-0.5">實際採購金額 × 收費比例</div>
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">數量</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">折扣後小計 (元)</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">操作</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {markupRows.map(({ item, index }) => {
                  const display = getQuoteLineDisplay({
                    listPrice: item.contract_price ?? 0,
                    contractPrice: item.contract_price ?? 0,
                    quantity: item.quantity ?? 0,
                    unit: item.unit || '%',
                    forceMarkup: true,
                    isDeduction: item.is_deduction
                  });
                  const chargePercent = storedRateToPercent(item.contract_price || 0, item.unit || '%');
                  return (
                    <tr key={`markup-${index}`} className={item.is_deduction ? 'bg-red-50' : ''}>
                      {renderDeductionCell(item, index)}
                      {renderItemNameCell(item, index)}
                      <td className="px-4 py-2">
                        <select
                          value={item.service_type || ''}
                          onChange={(e) => handleProcurementTypeChange(index, e.target.value)}
                          className={rowInputClass(item.is_deduction)}
                          aria-label="收費類別"
                        >
                          <option value="">選擇收費類別</option>
                          {PROCUREMENT_SERVICE_TYPES.map(typeName => (
                            <option key={typeName} value={typeName}>
                              {typeName}
                            </option>
                          ))}
                          {item.service_type && !isProcurementServiceType(item.service_type) && (
                            <option value={item.service_type}>{item.service_type}（歷史項目）</option>
                          )}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            min={item.is_deduction ? undefined : '0'}
                            step="0.01"
                            placeholder="實際採購金額"
                            aria-label="實際採購金額"
                            value={quantityInputValue(item.quantity)}
                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                            className={`${rowInputClass(item.is_deduction)} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                          />
                          <span className="text-xs text-gray-500 whitespace-nowrap">元</span>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="100"
                            aria-label="收費比例"
                            value={quantityInputValue(chargePercent)}
                            onChange={(e) => handleChargePercentChange(index, e.target.value)}
                            className={`${rowInputClass(item.is_deduction)} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                          />
                          <span className="text-xs text-gray-500 whitespace-nowrap">%</span>
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className={`text-xs font-medium ${item.is_deduction ? 'text-red-600' : 'text-gray-700'}`}>
                          {(item.is_deduction ? '-' : '') + formatCurrency(Math.abs(display.contractPriceAmount))}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="text-xs text-gray-700 whitespace-nowrap">
                          {display.quantityDisplay} {display.quantityUnitDisplay}
                        </div>
                      </td>
                      {renderAfterDiscountCell(item, true)}
                      {renderRemoveCell(index)}
                    </tr>
                  );
                })}
                <tr className="bg-gray-50">
                  <td colSpan={7} className="px-4 py-2 text-xs font-medium text-ci-text text-right">採購類別合計</td>
                  <td className="px-4 py-2 text-xs font-semibold text-ci-text">{formatCurrency(procurementCategoryTotal)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
